import { FC, useContext } from 'react'
import { useTranslation } from 'react-i18next'

import { AuthSessionContext } from '@context/AuthContext'

import { Tooltip } from '@ui/Popups/Tooltip/Tooltip'
import { Body1 } from '@ui/Typography'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadSort,
  TableHeader,
  TableNoResults,
  TableRow,
} from '@ui/v2/Table/Table'
import { TableHeaderTooltip } from '@ui/v2/Table/TableHeaderTooltip'

import { PageQuery, SortOrder } from '@utils/ApiClient'
import { formatDate } from '@utils/date.utils'

import { NameDto } from '@model/NameDto'
import { RebateTransactionDto, RebateTransactionSortKeys } from '@model/RebateTransactionDto'

import { StatusBadge } from '@global/badge/StatusBadge'

import { useFormatNumber } from '@hooks/useFormatNumber'

interface TransactionsPageTableProps {
  rebateTransactions: RebateTransactionDto[]
  sortOptions?: NameDto<RebateTransactionSortKeys | null>[]
  pageQuery: PageQuery | undefined
  sticky?: 'left'
  setPageQuery?: (value: PageQuery) => void
}

export const TransactionsPageTable: FC<TransactionsPageTableProps> = (props) => {
  const { rebateTransactions, sortOptions, pageQuery, sticky, setPageQuery } = props
  const { formatNumber } = useFormatNumber()
  const [auth] = useContext(AuthSessionContext)
  const { t } = useTranslation()
  const dateFormat = auth?.dateFormatType?.name
  const defaultSort = 'transactionDate'
  const defaultSortOrder: SortOrder = 'DESC'

  return (
    <Table sticky={sticky}>
      <TableHeader>
        <TableRow>
          {sortOptions?.map((option) =>
            option.id ? (
              <TableHeadSort
                id={option.id}
                key={option.id}
                sort={pageQuery?.sort || defaultSort}
                sortOrder={pageQuery?.sortOrder ?? defaultSortOrder}
                setSort={(sort, sortOrder) => {
                  setPageQuery?.({
                    ...pageQuery,
                    sort,
                    sortOrder,
                  })
                }}
              >
                {option.name}
                <TableHeaderTooltip<Partial<Record<RebateTransactionSortKeys, string>>>
                  headerIds={{
                    accumulatedVolumeFxm: t('IB.RebatesPage.Forex & Metals'),
                    accumulatedVolumeOther: t('IB.RebatesPage.Other instrument groups'),
                    accumulatedCommission: t('IB.RebatesPage.Commission accrued for TA'),
                  }}
                  targetHeaderId={option.id}
                />
              </TableHeadSort>
            ) : (
              <TableHead key={option.name}>{option.name}</TableHead>
            )
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {rebateTransactions.map((transaction) => (
          <TableRow key={transaction.id}>
            <TableCell width={157} alert={transaction.showNotification}>
              {formatDate(transaction.date, { formatType: dateFormat, withTime: false })}
            </TableCell>
            <TableCell width={157}>{transaction.tradingAccount?.name}</TableCell>
            <TableCell width={157}>{transaction.tradingAccountType?.name}</TableCell>
            <TableCell width={157}>
              <Tooltip.New
                content={formatNumber(transaction.accumulatedVolumeFxm)}
                disabled={transaction.accumulatedVolumeFxm < 100_000_000}
              >
                {formatNumber(transaction.accumulatedVolumeFxm, 9)}
              </Tooltip.New>
            </TableCell>
            <TableCell width={150}>
              <Tooltip.New
                content={formatNumber(transaction.accumulatedVolumeOther)}
                disabled={transaction.accumulatedVolumeOther < 100_000_000}
              >
                {formatNumber(transaction.accumulatedVolumeOther, 9)}
              </Tooltip.New>
            </TableCell>
            <TableCell width={150}>{formatNumber(transaction.accumulatedCommission, 10)}</TableCell>
            <TableCell width={150}>{formatNumber(transaction.rebateAmount, 10)}</TableCell>
            <TableCell>
              <StatusBadge.RebateTransactions status={transaction.state.id} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

interface EmptyTransactionsPageTableProps {
  isAnyFilterActive?: boolean
}

export const EmptyTransactionsPageTable: FC<EmptyTransactionsPageTableProps> = (props) => {
  const { isAnyFilterActive } = props

  const { t } = useTranslation()

  return (
    <TableNoResults
      title={
        isAnyFilterActive
          ? t('IB.RebatesPage.Nothing to show')
          : t('IB.RebatesPage.TransactionsPage.No transactions to display')
      }
      content={
        isAnyFilterActive ? <Body1>{t('IB.RebatesPage.Please, adjust your filters')}</Body1> : null
      }
    />
  )
}
